import React, { useEffect, useState } from "react";
import Button, {
  ButtonTypes,
  ButtonColorTypes,
} from "../shared-components/Button";
import InputField from "../shared-components/InputField";
import Divider from "../shared-components/LineDivider";
import { uniqueUUID } from "../utils";
import { PersonFormStateError } from "./PersonFormErrorState";
import PersonFormStringDate from "./PersonFormStringDate";

export interface EducationDefinitions {
  degree: string;
  institution: string;
  startDate: string;
  endDate: string;
  country: string;
  key: string;
}

type Props = {
  onChange?: (aliases: EducationDefinitions[]) => void;
  error?: PersonFormStateError;
  prefill?: EducationDefinitions[];
  alternateColor?: boolean;
};

function buildEmptyDefinition(): EducationDefinitions {
  return {
    degree: "",
    institution: "",
    startDate: "",
    endDate: "",
    country: "",
    key: uniqueUUID(),
  };
}

const PersonFormEducation = ({
  onChange,
  error,
  prefill,
  alternateColor,
}: Props) => {
  const [fieldsList, setFields] = useState<EducationDefinitions[]>([]);

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (
    idx: number,
    field: "degree" | "institution" | "startDate" | "endDate" | "country",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: EducationDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length >= 1) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <>
      {fieldsList.map((fields, idx) => (
        <div key={fields.key} className="flex flex-wrap">
          <InputField
            handleChange={(e) => {
              updateField(idx, "degree", e.target.value);
            }}
            name={`degree-${idx}`}
            value={fieldsList[idx].degree}
            placeholder=""
            label={"Degree"}
            classes="mb-4 mr-8"
          />
          <InputField
            handleChange={(e) => {
              updateField(idx, "institution", e.target.value);
            }}
            name={`institution-${idx}`}
            placeholder=""
            value={fieldsList[idx].institution}
            label={"Name of Institution"}
            classes={"mb-4 mr-8"}
          />
          <PersonFormStringDate
            index={idx}
            error={error}
            _name={`start-date-{idx}`}
            _label="Start Date"
            prefill={fieldsList[idx].startDate}
            onChange={(e) => {
              updateField(idx, "startDate", e);
            }}
          ></PersonFormStringDate>
          <PersonFormStringDate
            error={error}
            _name={`end-date-{idx}`}
            _label="End Date"
            index={idx}
            prefill={fieldsList[idx].endDate}
            onChange={(e) => {
              updateField(idx, "endDate", e);
            }}
          ></PersonFormStringDate>
          <InputField
            handleChange={(e) => {
              updateField(idx, "country", e.target.value);
            }}
            name={`country-${idx}`}
            placeholder=""
            value={fieldsList[idx].country}
            label={"Country"}
            classes={"mb-4 mr-8"}
          />
          {idx >= 0 && (
            <div className="w-full">
              <Button
                type={ButtonTypes.remove}
                text="Remove"
                colorType={ButtonColorTypes.negativeAction}
                onClick={(e) => handleRemove(e, idx)}
                className="mt-4 px-0"
              />
            </div>
          )}
          <Divider className="mb-6" />
        </div>
      ))}
      <Button
        type={ButtonTypes.add}
        text="Add Education"
        colorType={
          alternateColor ? ButtonColorTypes.alternate : ButtonColorTypes.action
        }
        onClick={handleAdd}
      />
    </>
  );
};

export default PersonFormEducation;
